import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { customEquipmentTypeState, equipmentForModelState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { EquipmentTypeId, Model } from '@rock/types';
import { type FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatRemapSummary, remapEquipmentType } from './remapApi';
import { RemapTypeDialog } from './RemapTypeDialog';

/**
 * Shown when a model lives on a home-scoped custom type (`c:{cuid}`): its knowledge — manual, specs,
 * coverage reach — is fenced to one home, and the home gets none of a catalog type's
 * considerations/tasks. Two ways out, both through the `remapEquipmentType` callable: remap here when
 * the catalog already has the right type, or follow the candidate link and promote a new type first.
 * Remapping needs a home to act on, so the action waits for the equipment subscription.
 */
export const CustomTypeBanner: FC<{ model: Model }> = ({ model }) => {
    const equipment = useAsyncAtomValue(
        useMemo(() => equipmentForModelState(model.id), [model.id]),
        [],
    );
    const homeId = equipment[0]?.homeId;
    const custom = useAsyncAtomValue(
        useMemo(
            () => customEquipmentTypeState(homeId ? { homeId, typeId: model.typeId } : undefined),
            [homeId, model.typeId],
        ),
    );
    const [dialogOpen, setDialogOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    const handleRemap = async (targetTypeId: EquipmentTypeId) => {
        if (!homeId) return;
        setBusy(true);
        try {
            const result = await remapEquipmentType({ homeId, customTypeId: model.typeId, targetTypeId });
            toast.success(formatRemapSummary(result));
            // The model keeps its id (ids are opaque); its `typeId` flips in place and this banner
            // disappears with the subscription's next value.
            setDialogOpen(false);
        } catch (error) {
            toast.error('Remap failed', {
                description: error instanceof Error ? error.message : String(error),
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Card className="border-amber-300 bg-amber-50">
            <CardHeader className="pb-2">
                <CardTitle className="text-base">
                    Custom type: {custom?.title ?? model.details?.type ?? model.typeId}
                </CardTitle>
                <CardDescription>
                    This model lives on a home-scoped custom type — its manual and specs are fenced to one home, and the
                    home gets no catalog considerations or tasks.{' '}
                    {custom?.replacedBy
                        ? `Already remapped to ${custom.replacedBy}; this model was left for manual attention.`
                        : 'Remap it to a catalog type, or promote the candidate into a new one.'}
                </CardDescription>
            </CardHeader>
            <CardContent className="flex items-center gap-2 pt-0">
                <Button size="sm" onClick={() => setDialogOpen(true)} disabled={!homeId || busy}>
                    Remap to catalog type…
                </Button>
                {custom?.candidateId && (
                    <Button size="sm" variant="outline" asChild>
                        <Link to={`/equipment/candidates/${encodeURIComponent(custom.candidateId)}`}>
                            View candidate
                        </Link>
                    </Button>
                )}
                {!homeId && (
                    <span className="text-xs text-muted-foreground">
                        No equipment references this model, so there is no home to remap.
                    </span>
                )}
            </CardContent>
            <RemapTypeDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                title="Remap to a catalog type"
                description="The home's equipment moves onto the chosen type, this model moves onto it (id and manual unchanged), and the home re-eval picks up the type's tasks."
                spaceId={model.spaceId}
                initialQuery={custom?.title ?? model.details?.type}
                busy={busy}
                onConfirm={handleRemap}
            />
        </Card>
    );
};
